import { Moon, Sun, Sunset } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import type { Theme } from '@/contexts/AppContext';

const THEME_OPTIONS: { value: Exclude<Theme, 'system'>; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light theme', icon: Sun },
  { value: 'dark', label: 'Dark theme', icon: Moon },
  { value: 'solar', label: 'Solar theme (warm, sepia-tinted)', icon: Sunset },
];

/**
 * A row of icon buttons for picking the app's color theme — light,
 * dark, or "solar" (a warm, sepia-tinted reading theme; see the
 * `.solar` CSS variable block in `src/index.css`). Meant to be dropped
 * directly into a `DropdownMenuContent`, below the "Network" item.
 *
 * Deliberately plain buttons rather than `DropdownMenuItem`s stacked
 * vertically — three icon-only toggles read faster as a single row,
 * and (per Radix's dropdown-menu semantics) clicking one doesn't need
 * to close the whole account menu the way a normal item selection
 * would.
 */
export function ThemeMenuItem() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="px-2 py-1.5">
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">Theme</p>
      <div className="flex gap-1">
        {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
          const active = theme === value;
          return (
            <button
              key={value}
              type="button"
              onClick={(e) => {
                // Keep the dropdown open — picking a theme is a
                // "try it and see" action, not a one-shot navigation.
                e.preventDefault();
                setTheme(value);
              }}
              aria-label={label}
              aria-pressed={active}
              title={label}
              className={cn(
                'flex flex-1 items-center justify-center rounded-md border py-1.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              <Icon className="size-4" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
